import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  MonitorSmartphone,
  Wifi,
  WifiOff,
  AlertTriangle,
  CreditCard,
  RefreshCw,
  Printer,
  MapPin,
  Smartphone,
  Search,
  Siren,
  CheckCircle2,
  Clock,
  KeyRound,
  Unplug,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API } from '@/config/api';

const STATUS_BADGE = {
  online: {
    label: 'Online',
    className: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: <Wifi className="h-3 w-3" />,
  },
  stale: {
    label: 'Stale',
    className: 'bg-amber-100 text-amber-900 border-amber-200',
    icon: <AlertTriangle className="h-3 w-3" />,
  },
  offline: {
    label: 'Offline',
    className: 'bg-rose-100 text-rose-800 border-rose-200',
    icon: <WifiOff className="h-3 w-3" />,
  },
};

function formatSeen(seconds) {
  if (seconds == null) return 'never';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * Full-page kiosk fleet view.  Read-only — surfaces what the backend already
 * knows from POST /api/kiosk/register.  No mutating buttons.
 */
export default function KioskFleetPage() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | online | stale | offline | misconfigured
  const [q, setQ] = useState('');

  const fetchFleet = useCallback(async () => {
    setLoading(true);
    try {
      const token =
        localStorage.getItem('backoffice_token') ||
        localStorage.getItem('merchant_token') ||
        localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [fleetRes, incidentsRes] = await Promise.all([
        axios.get(`${API}/owner/kiosks/fleet-health`, { headers }),
        axios
          .get(`${API}/owner/kiosks/incidents`, { headers })
          .catch(() => ({ data: { active: [], recently_resolved: [] } })),
      ]);
      setData(fleetRes.data);
      setIncidents(incidentsRes.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Unable to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFleet();
    // 5-minute refresh interval — matches the tile.
    const t = setInterval(fetchFleet, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchFleet]);

  const summary = data?.summary;
  const kiosks = useMemo(() => data?.kiosks || [], [data]);

  const filtered = useMemo(() => {
    let list = kiosks;
    if (filter === 'online' || filter === 'stale' || filter === 'offline') {
      list = list.filter((k) => k.status === filter);
    } else if (filter === 'misconfigured') {
      list = list.filter((k) => !k.terminal_ready);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (k) =>
          (k.device_name || '').toLowerCase().includes(needle) ||
          (k.device_uuid || '').toLowerCase().includes(needle) ||
          (k.assigned_location?.label || '').toLowerCase().includes(needle),
      );
    }
    return list;
  }, [kiosks, filter, q]);

  return (
    <div data-testid="kiosk-fleet-page" className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MonitorSmartphone className="h-6 w-6 text-slate-600" />
            Kiosk Fleet
          </h1>
          <p className="text-sm text-slate-500">
            Kiosks set up for this restaurant and their health. Refreshes every 5 minutes.
          </p>
        </div>
        <PairKioskButton onPaired={fetchFleet} />
        <Button
          variant="outline"
          size="sm"
          onClick={fetchFleet}
          disabled={loading}
          data-testid="kiosk-fleet-page-refresh"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard
          testId="fleet-summary-total"
          icon={<MonitorSmartphone className="h-4 w-4 text-slate-500" />}
          label="Total"
          value={summary?.total ?? '—'}
          onClick={() => setFilter('all')}
          active={filter === 'all'}
        />
        <SummaryCard
          testId="fleet-summary-online"
          icon={<Wifi className="h-4 w-4 text-emerald-500" />}
          label="Online"
          value={summary?.online ?? '—'}
          valueClass="text-emerald-600"
          onClick={() => setFilter('online')}
          active={filter === 'online'}
        />
        <SummaryCard
          testId="fleet-summary-stale"
          icon={<AlertTriangle className="h-4 w-4 text-amber-500" />}
          label="Stale"
          value={summary?.stale ?? '—'}
          valueClass="text-amber-600"
          onClick={() => setFilter('stale')}
          active={filter === 'stale'}
        />
        <SummaryCard
          testId="fleet-summary-offline"
          icon={<WifiOff className="h-4 w-4 text-rose-500" />}
          label="Offline"
          value={summary?.offline ?? '—'}
          valueClass="text-rose-600"
          onClick={() => setFilter('offline')}
          active={filter === 'offline'}
        />
        <SummaryCard
          testId="fleet-summary-misconfigured"
          icon={<CreditCard className="h-4 w-4 text-amber-500" />}
          label="Terminal Not Ready"
          value={summary?.terminal_misconfigured ?? '—'}
          valueClass={
            (summary?.terminal_misconfigured ?? 0) > 0
              ? 'text-amber-700'
              : 'text-slate-700'
          }
          onClick={() => setFilter('misconfigured')}
          active={filter === 'misconfigured'}
        />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by name, UUID, or location"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
          data-testid="kiosk-fleet-search-input"
        />
      </div>

      {/* Active incidents */}
      <ActiveIncidentsSection incidents={incidents} />

      {/* Error / empty / list */}
      {error ? (
        <Card>
          <CardContent className="py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-slate-500">
            {loading
              ? 'Loading kiosks…'
              : 'No kiosks match the current filter.'}
          </CardContent>
        </Card>
      ) : (
        <div
          data-testid="kiosk-fleet-list"
          className="grid grid-cols-1 lg:grid-cols-2 gap-3"
        >
          {filtered.map((k) => (
            <KioskRow key={k.device_id} kiosk={k} onUnpaired={fetchFleet} />
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  valueClass = 'text-slate-800',
  onClick,
  active,
  testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-3 rounded-lg border transition-colors w-full ${
        active
          ? 'border-blue-400 bg-blue-50 dark:bg-blue-950'
          : 'border-slate-200 bg-white hover:border-slate-300 dark:bg-gray-900 dark:border-gray-800'
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
    </button>
  );
}

function KioskRow({ kiosk, onUnpaired }) {
  const status = STATUS_BADGE[kiosk.status] || STATUS_BADGE.offline;
  const pps = kiosk.payment_profile_status || {};
  const printer = kiosk.printer_settings_summary || {};

  return (
    <Card
      data-testid={`kiosk-row-${kiosk.device_uuid}`}
      className={`border ${
        kiosk.status === 'offline'
          ? 'border-rose-200'
          : kiosk.status === 'stale'
          ? 'border-amber-200'
          : 'border-slate-200'
      }`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold truncate">
              {kiosk.device_name || kiosk.device_uuid}
            </CardTitle>
            <p className="text-xs text-slate-500 truncate font-mono">
              {kiosk.device_uuid}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge
              variant="outline"
              className={`text-xs ${status.className} inline-flex items-center gap-1`}
              data-testid={`kiosk-row-status-${kiosk.device_uuid}`}
            >
              {status.icon}
              {status.label}
            </Badge>
            <button
              onClick={() => unpairKiosk(kiosk, onUnpaired)}
              className="text-xs text-slate-400 hover:text-rose-600 inline-flex items-center gap-1"
              data-testid={`kiosk-row-unpair-${kiosk.device_uuid}`}
            >
              <Unplug className="h-3 w-3" /> Unpair
            </button>
            {!kiosk.terminal_ready && (
              <Badge
                variant="outline"
                className="text-xs bg-amber-100 text-amber-900 border-amber-200 inline-flex items-center gap-1"
                data-testid={`kiosk-row-terminal-${kiosk.device_uuid}`}
              >
                <CreditCard className="h-3 w-3" />
                Terminal Not Ready
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 text-sm">
        <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs text-slate-600">
          <DetailRow
            icon={<Smartphone className="h-3.5 w-3.5" />}
            label="Platform"
            value={kiosk.platform || '—'}
          />
          <DetailRow
            label="App version"
            value={kiosk.app_version || '—'}
          />
          <DetailRow label="Model" value={kiosk.model || '—'} />
          <DetailRow
            icon={<MapPin className="h-3.5 w-3.5" />}
            label="Location"
            value={kiosk.assigned_location?.label || '—'}
          />
          <DetailRow
            label="Last seen"
            value={formatSeen(kiosk.seconds_since_seen)}
          />
          <DetailRow
            icon={<CreditCard className="h-3.5 w-3.5" />}
            label="Card reader"
            value={pps.card_ready ? 'Ready' : 'Not ready'}
          />
        </div>

        <IdleTimeoutControl kiosk={kiosk} />

        <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
          <Printer className="h-3.5 w-3.5 text-slate-400" />
          <span>
            Receipt: {printer.receipt_printer_enabled ? 'on' : 'off'} ·
            Kitchen: {printer.kitchen_printer_enabled ? 'on' : 'off'}
            {printer.configured ? '' : ' (defaults)'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

const IDLE_CHOICES = [30, 45, 60, 90, 120, 180, 300];

/** Kiosk idle timeout: PUT /kiosk-admin/kiosks/{id}/settings; the kiosk re-reads it from /config. */
function IdleTimeoutControl({ kiosk }) {
  const [saved, setSaved] = useState(kiosk.idle_timeout || 60);
  const [value, setValue] = useState(kiosk.idle_timeout || 60);
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  const [error, setError] = useState('');
  const choices = IDLE_CHOICES.includes(saved) ? IDLE_CHOICES : [...IDLE_CHOICES, saved].sort((a, b) => a - b);

  const save = async () => {
    setState('saving');
    setError('');
    try {
      const res = await axios.put(`${API}/kiosk-admin/kiosks/${kiosk.device_id}/settings`,
        { idle_timeout: Number(value) }, { headers: authHeaders() });
      const stored = Number(res.data?.settings?.idle_timeout);
      if (stored !== Number(value)) throw new Error('The server did not store the new timeout');
      setSaved(stored);
      setState('saved');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (e?.message || 'Could not save the idle timeout'));
      setState('error');
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid={`kiosk-idle-${kiosk.device_uuid}`}>
      <Clock className="h-3.5 w-3.5 text-slate-400" />
      <label htmlFor={`idle-${kiosk.device_id}`}>Back to start screen after</label>
      <select id={`idle-${kiosk.device_id}`} value={value} onChange={(e) => { setValue(Number(e.target.value)); setState('idle'); }}
        className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-900"
        data-testid={`kiosk-idle-select-${kiosk.device_uuid}`}>
        {choices.map((sec) => (
          <option key={sec} value={sec}>{sec % 60 ? `${sec} s` : `${sec / 60} min`}</option>
        ))}
      </select>
      <span>of no touch</span>
      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={save}
        disabled={state === 'saving' || Number(value) === saved} data-testid={`kiosk-idle-save-${kiosk.device_uuid}`}>
        {state === 'saving' ? 'Saving...' : 'Save'}
      </Button>
      {state === 'saved' && <span className="text-emerald-700" data-testid={`kiosk-idle-saved-${kiosk.device_uuid}`}>Saved — the kiosk picks it up on its next config read</span>}
      {state === 'error' && <span className="text-rose-700">{error}</span>}
    </div>
  );
}

function DetailRow({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[11px] text-slate-400 uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
        {value}
      </p>
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  return `${Math.floor(seconds / 86400)}d`;
}

function ActiveIncidentsSection({ incidents }) {
  if (!incidents) return null;
  const active = incidents.active || [];
  const resolved = incidents.recently_resolved || [];
  if (active.length === 0 && resolved.length === 0) return null;

  return (
    <Card
      data-testid="kiosk-fleet-incidents-section"
      className="border-rose-200"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Siren className="h-4 w-4 text-rose-500" />
          Active Incidents
          {active.length > 0 && (
            <Badge
              variant="outline"
              className="bg-rose-100 text-rose-800 border-rose-200"
            >
              {active.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {active.length === 0 ? (
          <p className="text-sm text-slate-500 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            No active incidents.
          </p>
        ) : (
          active.map((i) => <IncidentRow key={i.id} incident={i} />)
        )}

        {resolved.length > 0 && (
          <div className="pt-2 border-t border-slate-200 mt-2">
            <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">
              Recently resolved
            </p>
            <div className="space-y-1">
              {resolved.slice(0, 5).map((i) => (
                <IncidentRow key={i.id} incident={i} compact />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function IncidentRow({ incident, compact = false }) {
  const isOffline = incident.incident_type === 'offline';
  const subject = incident.kiosk_name || (isOffline ? 'Kiosk' : 'Merchant terminal');
  const Icon = isOffline ? WifiOff : CreditCard;
  const tone = incident.resolved_at
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : isOffline
    ? 'text-rose-700 bg-rose-50 border-rose-200'
    : 'text-amber-800 bg-amber-50 border-amber-200';

  return (
    <div
      data-testid={`kiosk-incident-${incident.id}`}
      className={`flex items-center justify-between gap-2 p-2 rounded-md border ${tone} ${
        compact ? 'text-xs py-1.5' : 'text-sm'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        <div className="min-w-0">
          <p className="font-medium truncate">
            {subject} — {isOffline ? 'offline' : 'terminal not configured'}
          </p>
          {!compact && (
            <p className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(incident.duration_seconds)}
              </span>
              {incident.alerted_at ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  SMS sent
                </span>
              ) : incident.resolved_at ? null : (
                <span className="inline-flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="h-3 w-3" />
                  alert pending
                </span>
              )}
              {incident.resolved_at && (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" />
                  resolved
                </span>
              )}
            </p>
          )}
        </div>
      </div>
      {compact && incident.resolved_at && (
        <span className="text-[10px] text-slate-400">
          {new Date(incident.resolved_at).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
}


function authHeaders() {
  const token =
    localStorage.getItem('backoffice_token') ||
    localStorage.getItem('merchant_token') ||
    localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}` };
}

async function unpairKiosk(kiosk, onDone) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Unpair ${kiosk.device_name || 'this kiosk'}? It stops taking orders until it is set up again.`)) return;
  try {
    await axios.post(`${API}/kiosk-admin/kiosks/${kiosk.device_id}/unpair`, {}, { headers: authHeaders() });
    onDone?.();
  } catch (e) {
    // eslint-disable-next-line no-alert
    window.alert(e?.response?.data?.detail || 'Unpair failed');
  }
}

/** One-time pairing code for a kiosk that has no manager on site to sign in. */
function PairKioskButton({ onPaired }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(`${API}/kiosk-admin/pairing-codes`, { kiosk_name: name }, { headers: authHeaders() });
      setCode(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create a code');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)} data-testid="pair-kiosk-btn">
        <KeyRound className="h-4 w-4 mr-1" /> Pair a kiosk
      </Button>
    );
  }
  return (
    <div className="w-full sm:w-auto rounded-lg border border-slate-200 p-3 space-y-2 bg-white dark:bg-slate-900" data-testid="pair-kiosk-panel">
      {code ? (
        <>
          <p className="text-sm">On the kiosk, open Setup → Pairing code and enter:</p>
          <p className="text-3xl font-mono font-bold tracking-widest" data-testid="pairing-code">{code.code}</p>
          <p className="text-xs text-slate-500">Works once, until {new Date(code.expires_at).toLocaleTimeString()}. Or sign in on the kiosk with a manager email and password.</p>
          <Button size="sm" variant="outline" onClick={() => { setOpen(false); setCode(null); setName(''); onPaired?.(); }}>Done</Button>
        </>
      ) : (
        <div className="flex gap-2 items-center">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kiosk name (e.g. Front Entrance)" className="h-9 w-64" />
          <Button size="sm" onClick={create} disabled={busy || !name.trim()}>Get code</Button>
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          {err && <span className="text-xs text-rose-600">{err}</span>}
        </div>
      )}
    </div>
  );
}
